import { isNil } from 'lodash';
import moment from 'moment';
import { API } from '@shared/types/api';
import {
  CardPaginationCardFilter,
  CardPaginationCardFilterOption,
  CardPaginationDateFilter,
  CardPaginationQuery,
  CardPaginationTagFilter,
} from './types';
import { CardPaginationUserFilter } from '.';

export function matchesQuery(card: API.CardFragment, query: CardPaginationQuery): boolean {
  const tagFilters = query.filters?.filter((f): f is CardPaginationTagFilter => f.type === 'tag');
  const cardFilters = query.filters?.filter(
    (f): f is CardPaginationCardFilter => f.type === 'card',
  );
  const userFilters = query.filters?.filter(
    (f): f is CardPaginationUserFilter => f.type === 'user',
  );

  const matchesChannel = !query.channelId || card.channelId === query.channelId;
  const matchesTag =
    !tagFilters?.length ||
    tagFilters.every(f => {
      const hasTag = (tag: { fieldName: string; value: string }) =>
        card.fields?.[tag.fieldName]?.includes(tag.value);
      const mustHaveAll = !f.mustHaveAll || f.mustHaveAll.every(hasTag);
      const mustHaveAny = !f.mustHaveAny || f.mustHaveAny.some(hasTag);
      const mustNotHaveAny = !f.mustNotHaveAny || !f.mustNotHaveAny.some(hasTag);
      return mustHaveAll && mustHaveAny && mustNotHaveAny;
    });

  const matchesCard =
    !cardFilters?.length ||
    cardFilters.every(f => {
      const hasCard = (option: CardPaginationCardFilterOption) =>
        card.fields?.[option.fieldName]?.some(c => c.id === option.cardId);
      const mustHaveAll = !f.mustHaveAll || f.mustHaveAll.every(hasCard);
      const mustHaveAny = !f.mustHaveAny || f.mustHaveAny.some(hasCard);
      const mustNotHaveAny = !f.mustNotHaveAny || !f.mustNotHaveAny.some(hasCard);
      return mustHaveAll && mustHaveAny && mustNotHaveAny;
    });

  const matchesAssignees =
    !userFilters?.length ||
    userFilters.every(f => {
      const hasAssignee = (userId: number) => card.assignees?.some(a => a.id === userId);
      const mustHaveAll = !f.mustHaveAllIds || f.mustHaveAllIds.every(hasAssignee);
      const mustHaveAny = !f.mustHaveAnyIds || f.mustHaveAnyIds.some(hasAssignee);
      const mustNotHaveAny = !f.mustNotHaveAnyIds || !f.mustNotHaveAnyIds.some(hasAssignee);
      return mustHaveAll && mustHaveAny && mustNotHaveAny;
    });

  const matchesStatus = !isNil(card.status) && query.statuses.includes(card.status);

  const startDateFilter = query.filters?.find(
    (f): f is CardPaginationDateFilter => f.type === 'startDate',
  );

  const matchesStartDateBefore =
    !startDateFilter?.before ||
    (!!card.startDate && moment(card.startDate).isSameOrBefore(startDateFilter.before));
  const matchesStartDateAfter =
    !startDateFilter?.after ||
    (!!card.startDate && moment(card.startDate).isSameOrAfter(startDateFilter.after));
  return (
    matchesChannel &&
    matchesTag &&
    matchesCard &&
    matchesAssignees &&
    matchesStatus &&
    matchesStartDateBefore &&
    matchesStartDateAfter
  );
}
